import * as React from 'react';

type DesktopNavZone = 'primary' | 'secondary' | 'content' | null;

interface SecondarySidebarState {
  storageKey: string | null;
  mounted: boolean;
}

interface DesktopSidebarHoverContextValue {
  primaryExpanded: boolean;
  secondaryExpanded: boolean;
  secondaryMounted: boolean;
  syncActiveZone: (zone: DesktopNavZone) => void;
  activatePrimary: () => void;
  activateSecondary: () => void;
  activateContent: () => void;
  clearDesktopNavigation: () => void;
  registerSecondarySidebar: (storageKey: string) => void;
  unregisterSecondarySidebar: (storageKey: string) => void;
}

const DesktopSidebarHoverContext = React.createContext<DesktopSidebarHoverContextValue | null>(
  null,
);

export function DesktopSidebarHoverProvider({ children }: { children: React.ReactNode }) {
  const [activeZone, setActiveZone] = React.useState<DesktopNavZone>(null);
  const [secondarySidebar, setSecondarySidebar] = React.useState<SecondarySidebarState>({
    storageKey: null,
    mounted: false,
  });

  const syncActiveZone = React.useCallback((zone: DesktopNavZone) => {
    setActiveZone((prev) => (prev === zone ? prev : zone));
  }, []);

  const activatePrimary = React.useCallback(() => {
    syncActiveZone('primary');
  }, [syncActiveZone]);

  const activateSecondary = React.useCallback(() => {
    syncActiveZone('secondary');
  }, [syncActiveZone]);

  const activateContent = React.useCallback(() => {
    syncActiveZone('content');
  }, [syncActiveZone]);

  const clearDesktopNavigation = React.useCallback(() => {
    syncActiveZone(null);
  }, [syncActiveZone]);

  const registerSecondarySidebar = React.useCallback((storageKey: string) => {
    setSecondarySidebar((prev) => {
      if (prev.storageKey === storageKey && prev.mounted) {
        return prev;
      }

      return {
        storageKey,
        mounted: true,
      };
    });
  }, []);

  const unregisterSecondarySidebar = React.useCallback((storageKey: string) => {
    setSecondarySidebar((prev) =>
      prev.storageKey === storageKey
        ? {
            storageKey: null,
            mounted: false,
          }
        : prev,
    );
  }, []);

  const value = React.useMemo<DesktopSidebarHoverContextValue>(
    () => ({
      primaryExpanded: activeZone === 'primary',
      secondaryExpanded:
        secondarySidebar.mounted && (activeZone === 'primary' || activeZone === 'secondary'),
      secondaryMounted: secondarySidebar.mounted,
      syncActiveZone,
      activatePrimary,
      activateSecondary,
      activateContent,
      clearDesktopNavigation,
      registerSecondarySidebar,
      unregisterSecondarySidebar,
    }),
    [
      activeZone,
      activateContent,
      activatePrimary,
      activateSecondary,
      clearDesktopNavigation,
      registerSecondarySidebar,
      secondarySidebar.mounted,
      syncActiveZone,
      unregisterSecondarySidebar,
    ],
  );

  return (
    <DesktopSidebarHoverContext.Provider value={value}>
      {children}
    </DesktopSidebarHoverContext.Provider>
  );
}

export function useDesktopSidebarHover() {
  return React.useContext(DesktopSidebarHoverContext);
}
